import { Capacitor, registerPlugin } from "@capacitor/core";

/**
 * 보드 직결 경광등 — 현장 모니터 보드(RK3568 안드로이드)의 시리얼(/dev/ttyUSB11, 9600)에
 * "1"/"0" 을 쓰는 네이티브 플러그인(android/…/AlarmLightPlugin.java)의 JS 면.
 *
 * 폰·웹에는 장치가 없다: `hasAlarmLight()` 는 안드로이드 네이티브에서만 true 이고, 그래도
 * 장치가 없으면 `status().available` 이 false 다. 호출부는 둘 다 조용히 지나간다.
 */
export interface AlarmLightStatus {
  available: boolean;
  port: string | null;
  on: boolean;
  lastWriteAt: number;
  error: string | null;
}
export interface AlarmLightResult {
  ok: boolean;
  on: boolean;
  port: string | null;
  error: string | null;
}
interface AlarmLightPlugin {
  status(): Promise<AlarmLightStatus>;
  set(o: { on: boolean; autoOffSec?: number }): Promise<AlarmLightResult>;
  test(o: { seconds?: number }): Promise<AlarmLightResult>;
}

const AlarmLight = registerPlugin<AlarmLightPlugin>("AlarmLight");

const OFFLINE: AlarmLightStatus = { available: false, port: null, on: false, lastWriteAt: 0, error: null };

export const hasAlarmLight = (): boolean =>
  Capacitor.isNativePlatform() && Capacitor.getPlatform() === "android";

export async function alarmLightStatus(): Promise<AlarmLightStatus> {
  if (!hasAlarmLight()) return OFFLINE;
  try {
    return await AlarmLight.status();
  } catch {
    return OFFLINE;
  }
}

/**
 * 켜기/끄기. `autoOffSec`(기본 60, 네이티브 쪽 기본값) 뒤 스스로 꺼진다 — 터치가 없는
 * 모니터 보드에서 켜진 채 남는 등을 아무도 못 끄기 때문. 0 이면 자동 소등 없음.
 */
export async function alarmLightSet(on: boolean, autoOffSec?: number): Promise<AlarmLightResult> {
  if (!hasAlarmLight()) return { ok: false, on: false, port: null, error: "unsupported" };
  try {
    return await AlarmLight.set(autoOffSec == null ? { on } : { on, autoOffSec });
  } catch (e) {
    return { ok: false, on: false, port: null, error: String(e) };
  }
}

export async function alarmLightTest(seconds = 3): Promise<AlarmLightResult> {
  if (!hasAlarmLight()) return { ok: false, on: false, port: null, error: "unsupported" };
  try {
    return await AlarmLight.test({ seconds });
  } catch (e) {
    return { ok: false, on: false, port: null, error: String(e) };
  }
}
